// src/pages/ReportsPage.tsx

import React, { useEffect, useState } from "react";
import { Box, Stack, Button } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AutorenewIcon from "@mui/icons-material/Autorenew";

import PageHeader from "../../components/common/PageHeader";
import DataTable from "../../components/common/DataTable";
import StatusChip from "../../components/common/StatusChip";

import { REPORT_STRINGS, COMMON_STRINGS } from "../../constants/strings";

import { getSupportTickets, updateTicketStatus } from "../../services/api";

import { useDispatch } from "react-redux";
import { showSnackbar } from "../../store/snackbarSlice";

// ✅ TYPE
import { ReportRow } from "../../types";

const ReportsPage = () => {
  const dispatch = useDispatch();

  const [rows, setRows] = useState<ReportRow[]>([]);
  const [loading, setLoading] = useState(false);

  const [searchText, setSearchText] = useState("");
  const [typeFilter, setTypeFilter] = useState("");

  // ================= FETCH =================
  const fetchReports = async () => {
    try {
      setLoading(true);

      const res = await getSupportTickets();
      const apiData = res?.data?.data || [];

      // 🔥 FORMAT LIKE NOTIFICATION
      const formatted: ReportRow[] = apiData.map((item: any) => ({
        id: item._id,

        name: item.user?.name || REPORT_STRINGS.GUEST_USER,

        mobile: item.user?.mobile || "-",

        type: item.type || "-",
        description: item.description || REPORT_STRINGS.NO_DESCRIPTION,

        date: item.createdAt
          ? new Date(item.createdAt).toLocaleDateString("en-IN")
          : "-",

        status: item.status || "pending",
      }));

      setRows(formatted);
    } catch (err: any) {
      dispatch(
        showSnackbar({
          message: err.message || COMMON_STRINGS.SOMETHING_WENT_WRONG,
          type: "error",
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, []);

  // ================= STATUS UPDATE =================
  const handleStatusChange = async (
    id: string,
    status: ReportRow["status"],
  ) => {
    try {
      // optimistic update
      setRows((prev) =>
        prev.map((row) => (row.id === id ? { ...row, status } : row)),
      );

      await updateTicketStatus(id, status);

      dispatch(
        showSnackbar({
          message: `Ticket marked as ${status}`,
          type: "success",
        }),
      );
    } catch (err: any) {
      fetchReports();

      dispatch(
        showSnackbar({
          message: err.message || COMMON_STRINGS.SOMETHING_WENT_WRONG,
          type: "error",
        }),
      );
    }
  };

  // ================= FILTER =================
  const filteredRows = rows.filter((row) => {
    const search = searchText.toLowerCase();

    const matchSearch =
      row.name?.toLowerCase().includes(search) ||
      row.mobile?.includes(searchText) ||
      row.type?.toLowerCase().includes(search) ||
      row.description?.toLowerCase().includes(search);

    const matchType = typeFilter ? row.type === typeFilter : true;

    return matchSearch && matchType;
  });

  // ================= COLUMNS =================
  const columns: GridColDef<ReportRow>[] = [
    {
      field: "name",
      headerName: REPORT_STRINGS.COLUMN_NAME,
      flex: 1.5,
    },
    {
      field: "mobile",
      headerName: REPORT_STRINGS.COLUMN_MOBILE,
      flex: 1,
    },
    {
      field: "type",
      headerName: REPORT_STRINGS.COLUMN_TYPE,
      flex: 1,
    },
    {
      field: "description",
      headerName: REPORT_STRINGS.COLUMN_DESCRIPTION,
      flex: 2,
    },
    {
      field: "date",
      headerName: REPORT_STRINGS.COLUMN_DATE,
      flex: 1,
    },
    {
      field: "status",
      headerName: REPORT_STRINGS.COLUMN_STATUS,
      flex: 1,
      renderCell: (params) => <StatusChip status={params.value} />,
    },
    {
      field: "actions",
      headerName: REPORT_STRINGS.COLUMN_ACTIONS,
      flex: 3,
      sortable: false,
      renderCell: (params) => {
        const currentStatus = params.row.status;

        return (
          <Box className="action-cell">
  <Stack direction="row" spacing={1}>
    {/* 🔵 IN PROGRESS */}
    <Button
      size="small"
      variant="contained"
      startIcon={<AutorenewIcon />}
      disabled={currentStatus === "in_progress"}
      onClick={() =>
        handleStatusChange(params.row.id, "in_progress")
      }
      sx={{
        backgroundColor:
          currentStatus === "in_progress" ? "#d1d5db" : "#3b82f6", // 🔵 BLUE
        color:
          currentStatus === "in_progress" ? "#888" : "#fff",
        textTransform: "none",
        borderRadius: "8px",
      }}
    >
      In Progress
    </Button>

    {/* 🟢 RESOLVE */}
    <Button
      size="small"
      variant="contained"
      startIcon={<CheckCircleIcon />}
      disabled={currentStatus === "resolved"}
      onClick={() =>
        handleStatusChange(params.row.id, "resolved")
      }
      sx={{
        backgroundColor:
          currentStatus === "resolved" ? "#d1d5db" : "#22c55e", // 🟢 GREEN
        color:
          currentStatus === "resolved" ? "#888" : "#fff",
        textTransform: "none",
        borderRadius: "8px",
      }}
    >
      Resolve
    </Button>
  </Stack>
</Box>
        );
      },
    },
  ];

  return (
    <Box>
      <PageHeader
        title={REPORT_STRINGS.TITLE}
        searchValue={searchText}
        onSearchChange={setSearchText}
        filterValue={typeFilter}
        onFilterChange={setTypeFilter}
        filterOptions={[
          { label: "All", value: "" },
          { label: "Bug", value: "bug" },
          { label: "Payment", value: "payment" },
          { label: "Provider", value: "provider" },
        ]}
      />

      <DataTable
        rows={filteredRows}
        columns={columns}
        loading={loading}
        getRowId={(row) => row.id}
      />
    </Box>
  );
};

export default ReportsPage;
